/*
 * Switch: an immediate on/off setting. With `label`, the row is the hit area.
 *   <Switch checked={autoTopUp} onCheckedChange={setAutoTopUp} label={…} description={…} />
 */
import { Switch as S } from 'radix-ui'
import { useId, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
  id: idProp,
  className,
  'aria-label': ariaLabel,
}: {
  checked: boolean
  onCheckedChange: (v: boolean) => void
  label?: ReactNode
  description?: ReactNode
  disabled?: boolean
  id?: string
  className?: string
  'aria-label'?: string
}) {
  const auto = useId()
  const id = idProp ?? `sw${auto.replace(/:/g, '')}`
  const control = (
    <S.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className="relative inline-flex h-6 w-10 shrink-0 items-center rounded-full bg-line transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-brand-600"
    >
      <S.Thumb className="block size-5 translate-x-0.5 rounded-full bg-white shadow-sm transition-transform duration-200 will-change-transform data-[state=checked]:translate-x-[18px] rtl:-translate-x-0.5 rtl:data-[state=checked]:-translate-x-[18px]" />
    </S.Root>
  )
  if (!label && !description) return <span className={className}>{control}</span>
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <label htmlFor={id} className="flex min-w-0 cursor-pointer flex-col gap-0.5">
        {label ? <span className="text-14 font-medium text-ink">{label}</span> : null}
        {description ? <span className="text-13 text-subtle">{description}</span> : null}
      </label>
      <span className="pt-0.5">{control}</span>
    </div>
  )
}
